import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/tv" },
  { label: "综艺", to: "/category/variety" },
  { label: "资讯", to: "/news" },
  { label: "关于", to: "/about" },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  };

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    if (to === "/category/movie") return location.pathname === "/category/movie";
    if (to === "/category/tv") return location.pathname === "/category/tv";
    if (to === "/category/variety") return location.pathname === "/category/variety";
    return location.pathname.startsWith(to);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors ${
        scrolled
          ? "border-background-200 bg-background-50/95 backdrop-blur-md"
          : "border-transparent bg-background-50"
      }`}
    >
      <div className="mx-auto flex h-16 w-full max-w-7xl items-center gap-4 px-4 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-600 text-background-50">
            <i className="ri-movie-2-fill text-xl" aria-hidden="true" />
          </span>
          <span className="font-heading text-xl font-black tracking-tight text-primary-700">
            17C<span className="text-accent-500">.COM</span>
          </span>
        </Link>

        <nav className="hidden md:ml-4 md:flex md:flex-1 md:items-center md:gap-1">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                isActive(item.to)
                  ? "bg-primary-50 text-primary-700"
                  : "text-foreground-700 hover:bg-background-100 hover:text-primary-700"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <form
          onSubmit={handleSearch}
          className="ml-auto hidden items-center md:flex"
        >
          <div className="relative">
            <i
              className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"
              aria-hidden="true"
            />
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影视名称、类型、演员"
              aria-label="搜索影视"
              className="w-52 rounded-md border border-background-300 bg-background-100 py-2 pl-9 pr-3 text-sm text-foreground-800 outline-none transition-all placeholder:text-foreground-400 focus:w-64 focus:border-primary-400 focus:bg-background-50"
            />
          </div>
        </form>

        <Link
          to="/favorites"
          aria-label="我的收藏"
          className="ml-auto flex h-9 w-9 items-center justify-center rounded-md text-foreground-600 transition-colors hover:bg-background-100 hover:text-accent-600 md:ml-3"
        >
          <i className="ri-heart-3-line text-xl" aria-hidden="true" />
        </Link>

        <button
          type="button"
          aria-label="打开菜单"
          onClick={() => setMenuOpen((v) => !v)}
          className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-700 transition-colors hover:bg-background-100 md:hidden"
        >
          <i className={menuOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"} aria-hidden="true" />
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 md:hidden">
          <div className="mx-auto max-w-7xl px-4 py-4">
            <form onSubmit={handleSearch} className="mb-3">
              <div className="relative">
                <i
                  className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"
                  aria-hidden="true"
                />
                <input
                  type="search"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索影视名称、类型、演员"
                  aria-label="搜索影视"
                  className="w-full rounded-md border border-background-300 bg-background-100 py-2.5 pl-9 pr-3 text-sm text-foreground-800 outline-none placeholder:text-foreground-400 focus:border-primary-400"
                />
              </div>
            </form>
            <nav className="grid grid-cols-2 gap-2">
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`rounded-md px-3 py-2.5 text-sm font-medium ${
                    isActive(item.to)
                      ? "bg-primary-50 text-primary-700"
                      : "bg-background-100 text-foreground-700"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              <Link
                to="/favorites"
                className="rounded-md bg-background-100 px-3 py-2.5 text-sm font-medium text-foreground-700"
              >
                我的收藏
              </Link>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}